import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';

import {ApiError} from '../../util/error';

import {
    fetchAiMonitoringAnswers,
    fetchAiMonitoringAvailableCompetitors,
    fetchAiMonitoringBrandAnalysis,
    fetchAiMonitoringConfig,
    fetchAiMonitoringRecommendations,
    fetchAiMonitoringRun,
    fetchAiMonitoringScores,
    startAiMonitoringRun,
    updateAiMonitoringConfig,
} from './ai-brand-monitoring-api';
import {
    AI_MONITORING_RUN_POLL_INTERVAL_MS,
    aiMonitoringQueryKey,
    isAiMonitoringRunTerminal,
} from './ai-brand-monitoring-const';
import {
    AiMonitoringAnswersParametersType,
    AiMonitoringAnswersType,
    AiMonitoringAvailableCompetitorType,
    AiMonitoringBrandAnalysisType,
    aiMonitoringConfigErrorSchema,
    AiMonitoringConfigErrorType,
    AiMonitoringConfigType,
    AiMonitoringConfigUpdatePayloadType,
    AiMonitoringRecommendationsType,
    AiMonitoringRunStartType,
    AiMonitoringRunType,
    AiMonitoringScoresParametersType,
    AiMonitoringScoresType,
} from './ai-brand-monitoring-type';

// Extracts the typed 422 config error body (limit_exceeded / unsupported_model / monitoring_not_configured).
export function getAiMonitoringConfigError(error: unknown): AiMonitoringConfigErrorType | null {
    if (!(error instanceof ApiError) || error.statusCode !== 422) {
        return null;
    }

    const parsed = aiMonitoringConfigErrorSchema.safeParse(error.jsonData);

    return parsed.success ? parsed.data : null;
}

export function isAiMonitoringRunInProgressError(error: unknown): boolean {
    return error instanceof ApiError && error.statusCode === 409;
}

export function useAiMonitoringConfig(brandId: number | null): UseQueryResult<AiMonitoringConfigType> {
    return useQuery(aiMonitoringQueryKey.config(brandId), () => fetchAiMonitoringConfig(brandId as number), {
        enabled: Boolean(brandId),
        retry: 0,
    });
}

export function useUpdateAiMonitoringConfig(
    brandId: number | null
): UseMutationResult<AiMonitoringConfigType, unknown, AiMonitoringConfigUpdatePayloadType> {
    const queryClient = useQueryClient();

    return useMutation(
        (payload: AiMonitoringConfigUpdatePayloadType) => updateAiMonitoringConfig(brandId as number, payload),
        {
            onSuccess: (data) => {
                queryClient.setQueryData(aiMonitoringQueryKey.config(brandId), data);
                queryClient.invalidateQueries(aiMonitoringQueryKey.config(brandId));
            },
        }
    );
}

export function useAiMonitoringAvailableCompetitors(
    brandId: number | null
): UseQueryResult<Array<AiMonitoringAvailableCompetitorType>> {
    return useQuery(
        aiMonitoringQueryKey.availableCompetitors(brandId),
        () => fetchAiMonitoringAvailableCompetitors(brandId as number),
        {
            enabled: Boolean(brandId),
            retry: 0,
        }
    );
}

export function useAiMonitoringRun(brandId: number | null, runId: string | null): UseQueryResult<AiMonitoringRunType> {
    return useQuery(
        aiMonitoringQueryKey.run(brandId, runId),
        () => fetchAiMonitoringRun(brandId as number, runId as string),
        {
            enabled: Boolean(brandId) && Boolean(runId),
            retry: 0,
            // The run is filled asynchronously by airequester; poll until it reaches a
            // terminal status, then stop.
            refetchInterval: (data) =>
                data && !isAiMonitoringRunTerminal(data.status) ? AI_MONITORING_RUN_POLL_INTERVAL_MS : false,
        }
    );
}

export function useAiMonitoringBrandAnalysis(
    brandId: number | null,
    runId: string | null
): UseQueryResult<AiMonitoringBrandAnalysisType> {
    return useQuery(
        aiMonitoringQueryKey.analysis(brandId, runId),
        () => fetchAiMonitoringBrandAnalysis(brandId as number, runId as string),
        {
            enabled: Boolean(brandId) && Boolean(runId),
            retry: 0,
        }
    );
}

export function useAiMonitoringScores(
    brandId: number | null,
    parameters: AiMonitoringScoresParametersType
): UseQueryResult<AiMonitoringScoresType> {
    return useQuery(
        aiMonitoringQueryKey.scores(brandId, parameters),
        () => fetchAiMonitoringScores(brandId as number, parameters),
        {
            enabled: Boolean(brandId),
            retry: 0,
        }
    );
}

export function useAiMonitoringAnswers(
    brandId: number | null,
    parameters: AiMonitoringAnswersParametersType
): UseQueryResult<AiMonitoringAnswersType> {
    return useQuery(
        aiMonitoringQueryKey.answers(brandId, parameters),
        () => fetchAiMonitoringAnswers(brandId as number, parameters),
        {
            enabled: Boolean(brandId) && Boolean(parameters.runId),
            keepPreviousData: true,
            retry: 0,
        }
    );
}

export function useAiMonitoringRecommendations(
    brandId: number | null,
    runId: string | null
): UseQueryResult<AiMonitoringRecommendationsType> {
    return useQuery(
        aiMonitoringQueryKey.recommendations(brandId, runId),
        () => fetchAiMonitoringRecommendations(brandId as number, runId as string),
        {
            enabled: Boolean(brandId) && Boolean(runId),
            retry: 0,
        }
    );
}

type StartRunCallbacksType = {
    onSuccess?: (data: AiMonitoringRunStartType) => void;
    onError?: (error: unknown) => void;
};

export function useStartAiMonitoringRun(
    brandId: number | null,
    callbacks: StartRunCallbacksType = {}
): UseMutationResult<AiMonitoringRunStartType, unknown, void> {
    return useMutation(() => startAiMonitoringRun(brandId as number), {
        // The run starts asynchronously (202, status queued/running) and is filled by
        // airequester over Kafka. The run-scoped queries (run detail / analysis / scores /
        // answers / recommendations) are invalidated on completion once polling sees a
        // terminal status (see the page effect).
        onSuccess: (data) => {
            callbacks.onSuccess?.(data);
        },
        onError: (error) => {
            callbacks.onError?.(error);
        },
    });
}
